import type { captureException } from '@sentry/react';
import type { ErrorInfo } from 'react';
import type { RootOptions } from 'react-dom/client';

/** Where a root error ends up: Sentry in production, a fake in tests. */
export type RootErrorCapture = (error: unknown, componentStack: string | undefined) => void;

/**
 * Bounds the buffer when Sentry never connects: in dev it isn't loaded at
 * all, and its chunk can fail to load (ad blockers).
 */
export const MAX_BUFFERED_ROOT_ERRORS = 20;

/**
 * Reports errors React hands to the root (`onUncaughtError` and friends) to
 * Sentry. Sentry loads lazily after first paint, so a crash on first load
 * arrives before there is anything to report to: those are buffered and
 * flushed, in order, when `connect` is called with the loaded capture.
 */
export function createRootErrorReporter() {
    let capture: RootErrorCapture | undefined;
    let pending: Array<[error: unknown, componentStack: string | undefined]> = [];

    return {
        report(error: unknown, { componentStack }: Pick<ErrorInfo, 'componentStack'>) {
            // React's ErrorInfo types a missing stack as null
            const stack = componentStack ?? undefined;
            if (capture) capture(error, stack);
            else if (pending.length < MAX_BUFFERED_ROOT_ERRORS) pending.push([error, stack]);
        },
        connect(next: RootErrorCapture) {
            capture = next;
            const buffered = pending;
            pending = [];
            for (const [error, stack] of buffered) next(error, stack);
        },
    };
}

/** Sentry's own React error handler files the component stack under the `react` context too. */
export const sentryRootErrorCapture =
    (sentry: { captureException: typeof captureException }): RootErrorCapture =>
    (error, componentStack) =>
        sentry.captureException(error, { contexts: { react: { componentStack } } });

type RootErrorReporter = Pick<ReturnType<typeof createRootErrorReporter>, 'report'>;

/**
 * `createRoot` options that hand every root error to the reporter. Passing a
 * hook replaces React's default console report, so each one logs too — in dev
 * Sentry never loads and the console is the only place the error shows up.
 */
export const rootErrorHooks = (reporter: RootErrorReporter) => {
    const handle = (error: unknown, errorInfo: Pick<ErrorInfo, 'componentStack'>) => {
        // biome-ignore lint/suspicious/noConsole: stands in for React's own report, which the hook replaces
        console.error(error, errorInfo.componentStack);
        reporter.report(error, errorInfo);
    };
    return {
        onUncaughtError: handle,
        onCaughtError: handle,
        onRecoverableError: handle,
    } satisfies RootOptions;
};

export const rootErrorReporter = createRootErrorReporter();
